import { native } from '@forest/native-services'
import { invalidSaleReporter, logNonFatal } from '~/lib/diagnostics'
import {
  deriveProductPricing,
  type ProductPricing,
} from '~/lib/effective-price'
import { validRemoteImageUrl } from '~/lib/remote-image'
import {
  readFirstPurchaseOffer,
  type ItemType,
  type ShopProductCollection,
} from './bootstrap'
import { isRecord } from './display-metadata'
import { loadNativeIapPrice, NativeIapPriceError } from './native-iap-price'
import { platformSku } from './platform-sku'

export type CurrencyKind = 'coin' | 'gem'

interface CurrencyProductBase {
  bonusPercent?: number
  collectionEntryId: number
  grant: number
  imageUrl: string
  productId: number
  title: string
}

export interface CoinProduct extends CurrencyProductBase {
  currency: 'coin'
  // The pack's Time Crystal cost — effective-price data, not a snapshot
  // (ADR 0027).
  pricing: ProductPricing
}

// A pack's First-purchase bonus as hydrated (GLOSSARY.md "First-purchase
// bonus (首購翻倍)"): the entry's eligible first-purchase offer, resolved to
// its bonus item type's gem value. Whether it is active depends on the Shop
// server clock — read it through activeFirstPurchaseBonus.
export interface FirstPurchaseBonus {
  // Extra Time Crystals on top of the pack's grant; never assumed to equal it.
  amount: number
  // null: no deadline.
  endsAtMs: number | null
  // The bonus item type's art the bonus Reward reveal presents (GLOSSARY.md
  // "First-purchase bonus art"); null: missing or invalid, so the reveal
  // presents the pack's own art.
  imageUrl: string | null
}

export interface TimeCrystalProduct extends CurrencyProductBase {
  currency: 'gem'
  firstPurchaseBonus?: FirstPurchaseBonus
  priceLocalized: string
  priceValue: number
  sku: string
}

export type CurrencyProduct = CoinProduct | TimeCrystalProduct

export interface CurrencyCatalogue {
  coin: CoinProduct[]
  gem: TimeCrystalProduct[]
}

interface CurrencyHydrationInput<Kind extends CurrencyKind = CurrencyKind> {
  collection: ShopProductCollection
  currency: Kind
  itemTypes: Map<number, RuntimeItemType>
}

type CurrencyProductByKind<Kind extends CurrencyKind> = Extract<
  CurrencyProduct,
  { currency: Kind }
>

type RuntimeCollectionEntry = Record<string, unknown> & { id: number }
type RuntimeItemType = Record<string, unknown> & { id: number }

const HYDRATION_CONCURRENCY = 4

function reportOmittedProduct(entry: unknown, reason: string): null {
  const entryRecord = isRecord(entry) ? entry : undefined
  const product = isRecord(entryRecord?.item_type_product)
    ? entryRecord.item_type_product
    : undefined
  console.error('[shop] omitted currency product', {
    collection_entry_id: entryRecord?.id,
    product_id: product?.id,
    reason,
  })
  return null
}

function isRuntimeEntry(value: unknown): value is RuntimeCollectionEntry {
  return isRecord(value) && isFiniteNumber(value.id)
}

function isRuntimeItemType(value: unknown): value is RuntimeItemType {
  return isRecord(value) && isFiniteNumber(value.id)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0
}

async function localizedTitle(itemType: RuntimeItemType): Promise<string> {
  const key = itemType.title_key
  if (typeof key !== 'string' || key.length === 0) return ''
  try {
    const title = await native.l10n.getLocalizedString({ key })
    return title.length > 0 && title !== key ? title : ''
  } catch (error) {
    console.error('[shop] currency product localization failed', {
      error,
      title_key: key,
    })
    return ''
  }
}

async function hydrateProduct(
  entry: RuntimeCollectionEntry,
  input: CurrencyHydrationInput,
): Promise<CurrencyProduct | null> {
  const product = entry.item_type_product
  if (!isRecord(product)) {
    return reportOmittedProduct(entry, 'missing product')
  }
  if (!isFiniteNumber(product.id)) {
    return reportOmittedProduct(entry, 'invalid product ID')
  }
  if (product.purchaseable !== true) {
    return reportOmittedProduct(entry, 'not purchaseable')
  }

  const expectedPurchaseType = input.currency === 'coin' ? 'gem' : 'iap'
  if (product.purchase_type !== expectedPurchaseType) {
    return reportOmittedProduct(entry, 'purchase type mismatch')
  }

  const itemType =
    typeof product.item_type_id === 'number'
      ? input.itemTypes.get(product.item_type_id)
      : undefined
  if (!itemType) return reportOmittedProduct(entry, 'missing item type')

  const contents = itemType.item_contents
  const content = Array.isArray(contents) ? contents[0] : undefined
  if (
    !isRecord(content) ||
    content.type !== input.currency ||
    !isPositiveNumber(content.value)
  ) {
    return reportOmittedProduct(entry, 'invalid first item content')
  }

  const imageUrl = validRemoteImageUrl(itemType.icon_image_url)
  if (!imageUrl) return reportOmittedProduct(entry, 'invalid artwork URL')

  if (input.currency === 'coin') {
    if (!isPositiveNumber(product.price)) {
      return reportOmittedProduct(entry, 'invalid Store price')
    }
    return {
      collectionEntryId: entry.id,
      currency: 'coin',
      grant: content.value,
      imageUrl,
      // Invalid sale data never omits the pack — the sale is ignored and
      // reported, and the pack sells at its original price (ADR 0027).
      pricing: deriveProductPricing(
        {
          price: product.price,
          sale_price: product.sale_price,
          sale_start_at: product.sale_start_at,
          sale_end_at: product.sale_end_at,
        },
        invalidSaleReporter(product.id),
      ),
      productId: product.id,
      title: await localizedTitle(itemType),
    }
  }

  const sku = platformSku(product)
  if (!sku) return reportOmittedProduct(entry, 'missing platform SKU')

  try {
    const price = await loadNativeIapPrice({ productId: product.id, sku })
    const firstPurchaseBonus = deriveFirstPurchaseBonus(
      entry,
      product.id,
      input.itemTypes,
    )
    return {
      collectionEntryId: entry.id,
      currency: 'gem',
      ...(firstPurchaseBonus ? { firstPurchaseBonus } : {}),
      grant: content.value,
      imageUrl,
      ...price,
      productId: product.id,
      sku,
      title: await localizedTitle(itemType),
    }
  } catch (error) {
    if (error instanceof NativeIapPriceError) {
      return reportOmittedProduct(entry, error.failure)
    }
    console.error('[shop] native currency price failed', {
      error,
      product_id: product.id,
    })
    return reportOmittedProduct(entry, 'native price failed')
  }
}

type FirstPurchaseBonusIgnoredReason =
  | 'invalid_bonus_item_type'
  | 'invalid_offer'
  | 'invalid_offers'
  | 'missing_bonus_item_type'
  | 'missing_offers'

function reportIgnoredFirstPurchaseBonus(
  reason: FirstPurchaseBonusIgnoredReason,
  productId: number,
): undefined {
  logNonFatal('shop_first_purchase_bonus_ignored', {
    reason,
    product_id: String(productId),
  })
  return undefined
}

// The entry's First-purchase bonus, or undefined when it has none. Bad data
// never removes the pack: it sells without a bonus and the gap is reported.
// An ineligible offer is not a gap, nor are other offer types.
function deriveFirstPurchaseBonus(
  entry: RuntimeCollectionEntry,
  productId: number,
  itemTypes: Map<number, RuntimeItemType>,
): FirstPurchaseBonus | undefined {
  const offers = entry.offers
  if (offers === undefined) {
    return reportIgnoredFirstPurchaseBonus('missing_offers', productId)
  }
  if (!Array.isArray(offers)) {
    return reportIgnoredFirstPurchaseBonus('invalid_offers', productId)
  }
  // A non-object element is no offer of any type: it is reported, and the
  // first-purchase offer is still read from the rest.
  if ((offers as unknown[]).some((each) => !isRecord(each))) {
    reportIgnoredFirstPurchaseBonus('invalid_offer', productId)
  }
  const offer = readFirstPurchaseOffer(offers)
  if (offer.kind === 'invalid') {
    return reportIgnoredFirstPurchaseBonus('invalid_offer', productId)
  }
  if (offer.kind === 'none') {
    return undefined
  }

  const bonusItemType = itemTypes.get(offer.bonusItemTypeId)
  if (!bonusItemType) {
    return reportIgnoredFirstPurchaseBonus('missing_bonus_item_type', productId)
  }
  const contents = bonusItemType.item_contents
  const content = Array.isArray(contents) ? contents[0] : undefined
  if (
    !isRecord(content) ||
    content.type !== 'gem' ||
    !isPositiveNumber(content.value)
  ) {
    return reportIgnoredFirstPurchaseBonus('invalid_bonus_item_type', productId)
  }
  // Missing art never removes the bonus: the backend grants it regardless.
  const imageUrl = validRemoteImageUrl(bonusItemType.icon_image_url)
  if (!imageUrl) {
    logNonFatal('shop_first_purchase_bonus_art_missing', {
      product_id: String(productId),
    })
  }
  return { amount: content.value, endsAtMs: offer.endsAtMs, imageUrl }
}

// A Time Crystal pack's active First-purchase bonus amount, or null — the
// one reading the cards, the notice and the purchase share.
export type FirstPurchaseBonusOf = (
  product: TimeCrystalProduct,
) => number | null

// Whether any of the packs has an active First-purchase bonus: the notice's,
// and the Time Crystals section's lead's, one reading.
export function anyFirstPurchaseBonusActive(
  products: readonly TimeCrystalProduct[],
  firstPurchaseBonusOf: FirstPurchaseBonusOf,
): boolean {
  return products.some((product) => firstPurchaseBonusOf(product) !== null)
}

// The bonus amount while the pack's First-purchase bonus is active at server
// now — `ends_at` null or later — and not yet spent this session, else null.
// A completed purchase of a pack spends its bonus (GLOSSARY.md "First-purchase
// bonus (首購翻倍)"); Shop does not refetch to confirm.
export function activeFirstPurchaseBonus(
  product: TimeCrystalProduct,
  serverNowMs: number,
  spentFirstPurchaseBonusProductIds: ReadonlySet<number>,
): number | null {
  const firstPurchaseBonus = product.firstPurchaseBonus
  if (
    !firstPurchaseBonus ||
    spentFirstPurchaseBonusProductIds.has(product.productId)
  ) {
    return null
  }
  if (
    firstPurchaseBonus.endsAtMs !== null &&
    firstPurchaseBonus.endsAtMs <= serverNowMs
  ) {
    return null
  }
  return firstPurchaseBonus.amount
}

function orderedEntries(collection: ShopProductCollection) {
  const rawEntries = collection.item_type_product_collection_products
  if (!Array.isArray(rawEntries)) return []

  const entries: RuntimeCollectionEntry[] = []
  for (const entry of rawEntries as unknown[]) {
    if (isRuntimeEntry(entry)) entries.push(entry)
    else reportOmittedProduct(entry, 'invalid collection entry')
  }

  return entries.sort((a, b) => {
    const aOrder =
      typeof a.display_order === 'number'
        ? a.display_order
        : Number.POSITIVE_INFINITY
    const bOrder =
      typeof b.display_order === 'number'
        ? b.display_order
        : Number.POSITIVE_INFINITY
    return aOrder - bOrder || a.id - b.id
  })
}

async function hydrateWithLimit(
  entries: RuntimeCollectionEntry[],
  input: CurrencyHydrationInput,
): Promise<(CurrencyProduct | null)[]> {
  const results = Array.from(
    { length: entries.length },
    (): CurrencyProduct | null => null,
  )
  let nextIndex = 0

  async function worker() {
    while (nextIndex < entries.length) {
      const index = nextIndex++
      results[index] = await hydrateProduct(entries[index]!, input)
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(entries.length, HYDRATION_CONCURRENCY) },
      worker,
    ),
  )
  return results
}

// The +N% value-per-price badge (GLOSSARY.md "Currency value bonus"). Pure
// over a parallel price array so callers can feed effective prices, which
// change with the server clock — the render layer applies this, not
// hydration.
export function applyValueBonuses<Product extends CurrencyProduct>(
  products: Product[],
  prices: number[],
): Product[] {
  let anchorIndex = -1
  products.forEach((product, index) => {
    if (anchorIndex === -1) {
      anchorIndex = index
      return
    }
    const price = prices[index]!
    const anchorPrice = prices[anchorIndex]!
    if (
      price < anchorPrice ||
      (price === anchorPrice && product.grant < products[anchorIndex]!.grant)
    ) {
      anchorIndex = index
    }
  })
  if (anchorIndex === -1) return products

  const anchor = products[anchorIndex]!
  const anchorPrice = prices[anchorIndex]!
  return products.map((product, index) => {
    const expectedGrant = (anchor.grant / anchorPrice) * prices[index]!
    const rawBonusPercent = (product.grant / expectedGrant - 1) * 100
    const bonusPercent = Math.floor(rawBonusPercent + 1e-9)
    return bonusPercent > 0 ? { ...product, bonusPercent } : product
  })
}

function isCurrencyProductOfKind<Kind extends CurrencyKind>(
  product: CurrencyProduct | null,
  kind: Kind,
): product is CurrencyProductByKind<Kind> {
  return product?.currency === kind
}

async function hydrateCollection<Kind extends CurrencyKind>(
  input: CurrencyHydrationInput<Kind>,
): Promise<CurrencyProductByKind<Kind>[]> {
  const products = await hydrateWithLimit(
    orderedEntries(input.collection),
    input,
  )
  // Bonuses are applied at render time (applyValueBonuses): coin-pack
  // effective prices move with the server clock, so hydration cannot bake
  // them in.
  return products.filter((product) =>
    isCurrencyProductOfKind(product, input.currency),
  )
}

export async function loadCurrencyCatalogue(input: {
  collections: {
    'shop-coin': ShopProductCollection
    'shop-gem': ShopProductCollection
  }
  itemTypes: ItemType[]
}): Promise<CurrencyCatalogue> {
  const itemTypes = new Map<number, RuntimeItemType>()
  if (Array.isArray(input.itemTypes)) {
    for (const itemType of input.itemTypes as unknown[]) {
      if (isRuntimeItemType(itemType)) itemTypes.set(itemType.id, itemType)
    }
  }
  const [coin, gem] = await Promise.all([
    hydrateCollection({
      collection: input.collections['shop-coin'],
      currency: 'coin',
      itemTypes,
    }),
    hydrateCollection({
      collection: input.collections['shop-gem'],
      currency: 'gem',
      itemTypes,
    }),
  ])
  return { coin, gem }
}

export type CurrencySource = Parameters<typeof loadCurrencyCatalogue>[0]

// Off the bootstrap critical path (ADR 0023): the Coins & Crystals tab shows
// sectioned skeletons while this hydrates, then commits both sections at
// once. Hydration cannot reject — per-product failures are omitted — so the
// query has no error state worth retrying.
export const currencyCatalogueQuery = (source: CurrencySource) => ({
  queryKey: ['shop', 'currency'] as const,
  queryFn: () => loadCurrencyCatalogue(source),
  gcTime: Infinity,
  retry: false,
  staleTime: Infinity,
})
